import { useEffect, useState } from 'react';
import {
  ArrowSquareOutIcon,
  ArrowsLeftRightIcon,
  CheckCircleIcon,
  CoinIcon,
  SignatureIcon,
  SpinnerIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { KeyValue } from '@/components/ui/key-value';

import {
  forwardWalletBatch,
  getTransferToBridgeTypedData,
  type TransferToBridgeTypedDataResponse,
} from '../api';
import { useAuth } from '../auth';
import { useT } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { tryGetTelegram } from '../telegram';
import { getSigner } from '../wallet';

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: TransferToBridgeTypedDataResponse }
  | { kind: 'signing' }
  | { kind: 'submitting' }
  | { kind: 'submitted'; transactionHash: string; state: string };

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;
const fmt = (raw: string) => `$${(Number(raw) / 1e6).toFixed(4)}`;

export default function Bridge() {
  const auth = useAuth();
  const t = useT();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useTelegramBackButton();
  useNavStackEntry({ label: t('bridge.breadcrumb') });

  const externalUserId =
    auth.state === 'ready' ? auth.auth.externalUserId : null;

  useEffect(() => {
    if (!externalUserId) return;
    let cancelled = false;
    (async () => {
      try {
        const usdceParam =
          new URLSearchParams(window.location.search).get('token') === 'usdc'
            ? ('usdc' as const)
            : ('usdce' as const);
        const data = await getTransferToBridgeTypedData(externalUserId, {
          token: usdceParam,
        });
        if (!cancelled) setPhase({ kind: 'ready', data });
      } catch (err) {
        if (!cancelled)
          setPhase({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [externalUserId]);

  async function onSign() {
    if (phase.kind !== 'ready' || !externalUserId) return;
    const { typedData, args } = phase.data;
    try {
      setPhase({ kind: 'signing' });
      const signer = await getSigner();
      const types = { ...typedData.types };
      delete (types as Record<string, unknown>)['EIP712Domain'];
      const signature = await signer.signTypedData(
        typedData.domain as never,
        types,
        typedData.message,
      );

      setPhase({ kind: 'submitting' });
      const resp = await forwardWalletBatch(externalUserId, {
        type: 'WALLET',
        from: args.from,
        to: args.factoryAddress,
        nonce: args.nonce,
        signature,
        depositWalletParams: {
          depositWallet: args.walletAddress,
          deadline: args.deadline,
          calls: args.calls,
        },
      });
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('success');
      setPhase({
        kind: 'submitted',
        transactionHash: resp.transactionHash,
        state: resp.state,
      });
    } catch (err) {
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('error');
      setPhase({ kind: 'error', message: (err as Error).message });
    }
  }

  if (auth.state === 'loading') {
    return (
      <AuthLoadingScreen
        title="Preparing the bridge"
        description="Authenticating your Telegram session before quoting the transfer."
        shortDescription="Authenticating your session."
      />
    );
  }
  if (phase.kind === 'loading') {
    return (
      <AuthLoadingScreen
        title="Quoting bridge transfer"
        description="Asking the Polymarket Bridge for an up-to-date typed payload."
        shortDescription="Quoting bridge transfer."
        steps={[
          { label: 'Session', color: 'bg-emerald-500' },
          { label: 'Wallet', color: 'bg-sky-500', delay: '0.4s' },
          { label: 'Bridge', color: 'bg-primary', delay: '0.8s' },
        ]}
      />
    );
  }

  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow="Bridge" title="Convert to pUSD">
        <Alert tone="danger">
          <AlertTitle>Authentication failed</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  if (phase.kind === 'error') {
    return (
      <ScreenShell eyebrow="Bridge" title="Convert to pUSD">
        <Alert tone="danger">
          <AlertTitle>Bridge transfer failed</AlertTitle>
          <AlertDescription>{phase.message}</AlertDescription>
        </Alert>
        <Button
          variant="outline"
          size="lg"
          onClick={() => tryGetTelegram()?.close?.()}
        >
          Back to chat
        </Button>
      </ScreenShell>
    );
  }

  if (phase.kind === 'submitted') {
    const url = phase.transactionHash
      ? `https://polygonscan.com/tx/${phase.transactionHash}`
      : null;
    return (
      <ScreenShell eyebrow="Bridge" title="Sent to bridge">
        <Card>
          <CardHeader>
            <CardEyebrow>
              <CheckCircleIcon weight="fill" className="size-3" />
              Submitted
            </CardEyebrow>
            <CardTitle>Funds on their way to Polymarket</CardTitle>
            <CardDescription>
              The Polymarket Bridge is converting your USDC and will credit pUSD
              to your deposit wallet in 1–3 minutes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <KeyValue label="State" value={<Badge tone="success">{phase.state}</Badge>} />
          </CardContent>
          <CardFooter>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: 'outline', size: 'lg' })}
              >
                <ArrowSquareOutIcon weight="bold" className="size-4" />
                View on Polygonscan
              </a>
            )}
            <Button
              variant="default"
              size="lg"
              onClick={() => tryGetTelegram()?.close?.()}
            >
              Back to chat
            </Button>
          </CardFooter>
        </Card>
      </ScreenShell>
    );
  }

  const data = phase.kind === 'ready' ? phase.data : null;
  const busy = phase.kind === 'signing' || phase.kind === 'submitting';

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <ArrowsLeftRightIcon weight="fill" className="size-3" />
          Bridge
        </span>
      }
      title="Convert to pUSD"
      description={`Sends your ${
        data?.token === 'usdc' ? 'USDC' : 'USDC.e'
      } balance to the Polymarket Bridge. The bridge converts it to pUSD and credits your deposit wallet within a few minutes — fully gasless.`}
    >
      <NavBar />
      <Card>
        <CardHeader>
          <CardEyebrow>
            <CoinIcon weight="fill" className="size-3" />
            Transfer details
          </CardEyebrow>
          <CardTitle className="text-3xl tabular-nums sm:text-4xl">
            {data ? fmt(data.amount) : '—'}
          </CardTitle>
          <CardDescription>
            {data?.token === 'usdc'
              ? 'Native USDC (Polygon)'
              : 'USDC.e (bridged on Polygon)'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <KeyValue
            label="Token"
            value={data?.token === 'usdc' ? 'USDC (native)' : 'USDC.e (bridged)'}
          />
          <KeyValue
            label="Bridge address"
            value={data ? short(data.bridgeAddress) : '—'}
            mono
          />
          <KeyValue label="Network" value="Polygon" />
          <KeyValue label="Gas" value={<Badge tone="success">Gasless</Badge>} />
        </CardContent>
        <CardFooter>
          <Button
            size="lg"
            onClick={onSign}
            disabled={phase.kind !== 'ready'}
          >
            {busy ? (
              <SpinnerIcon weight="bold" className="size-4 animate-spin" />
            ) : (
              <SignatureIcon weight="bold" className="size-4" />
            )}
            {phase.kind === 'signing'
              ? 'Signing…'
              : phase.kind === 'submitting'
                ? 'Submitting…'
                : 'Sign & send to bridge'}
          </Button>
        </CardFooter>
      </Card>
    </ScreenShell>
  );
}
